import type {MouseEvent, ReactElement, Ref} from 'react';
import React from 'react';
import './button.scss';
import {IconSize} from '@Components/icon-v2/icon.types';
import type {ButtonProps} from '@Components/button/button.types';
import {ButtonSize, IconPlacement} from '@Components/button/button.types';
import {getButtonAppearanceClass} from '@Utils/button.util';
import {assertNever} from '@Utils/assert-never.util';
import {Icon} from '../icon-v2';

const HREF_DEFAULT = 'javascript:void(0);';

/**
 * The design-system button. Its props map 1:1 to the Figma button's properties.
 *
 * Figma Dev Mapping tables: https://www.figma.com/design/NJibu7PjuokRgJNvkCaQtP/%F0%9F%9F%A2-Button?node-id=4469-2351
 */
export const Button = React.forwardRef(
  (buttonProps: ButtonProps, ref: Ref<any>): ReactElement<any> | null => {
    const {
      href = HREF_DEFAULT,
      id = '',
      size = ButtonSize.MEDIUM,
      customClasses = '',
      icon = '',
      iconClassName = '',
      loaderClassName = '',
      textClasses = '',
      iconPlacement = IconPlacement.LEFT,
      disabled = false,
      onClick = (): void => {},
      text = '',
      title = '',
      attributes = {},
      isLoading = false,
      isFullWidth = false,
      keepsWidthWhileLoading = false,
      isActive = false,
      showUpsell = false,
      dangerouslySetTextAsHTML = false,
      isSelected = false,
      ...props
    } = buttonProps;
    const hasUrl = href !== HREF_DEFAULT;
    const BtnTag = hasUrl ? 'a' : 'button';
    const classes = [
      'btn',
      size,
      getButtonAppearanceClass(buttonProps),
      iconPlacement,
      isLoading ? '-loading' : '',
      isFullWidth ? '-fullwidth' : '',
      keepsWidthWhileLoading ? '-keeps-width-while-loading' : '',
      customClasses,
      props.shortText !== undefined ? '-has-short-text' : '',
      text ? '' : '-no-text',
      isActive ? '-active' : '',
      isSelected ? '-selected' : '',
    ];

    hasUrl ? (attributes.href = href) : null;

    const getIconSize = (): IconSize => {
      switch (size) {
        case ButtonSize.SMALL:
          return IconSize.SIZE_ICON_12;
        case ButtonSize.MEDIUM:
          return IconSize.SIZE_ICON_16;
        case ButtonSize.LARGE:
          return IconSize.SIZE_ICON_16;
        case ButtonSize.EXTRA_LARGE:
          return IconSize.SIZE_ICON_24;
        default:
          return assertNever(size);
      }
    };

    const getPremiumUpsellIconSize = (): IconSize => {
      if (size === ButtonSize.SMALL) {
        return IconSize.SIZE_ICON_12;
      }
      return IconSize.SIZE_ICON_14;
    };

    const getLoader = (): ReactElement<any> => {
      return (
        <div className={`${loaderClassName} btn-loader`}>
          <progress className="icon-circular-loader" />
        </div>
      );
    };

    const getLoaderWithLoadingStateText = (): ReactElement<any> => {
      return (
        <div className={`${loaderClassName} _text-transform-none btn-loader`}>
          <progress className="icon-circular-loader" />
          <span className="spacing-m-l-2 btn-loader-text">{props.loadingStateText}</span>
        </div>
      );
    };

    const onClickHandler = (e: MouseEvent): void => {
      if (isLoading) {
        return;
      }

      if (typeof window.PMW?.gtm !== 'undefined' && props.ga4ClickName !== undefined && props.ga4ClickName !== '') {
        window.PMW.gtm.trackGA4CustomClick(props.ga4ClickName);
      }

      void onClick(e);
    };

    const renderIcon = (): ReactElement<any> | null => {
      if (props.customIcon) {
        return <span className="btn-icon">{props.customIcon}</span>;
      }

      if (icon) {
        return (
          <span className="btn-icon">
            <Icon icon={icon} className={iconClassName} size={getIconSize()} />
          </span>
        );
      }

      return null;
    };

    const getLongText = (): ReactElement<any> => {
      const textClasses = `-long ${props.shortText !== undefined ? '-has-short-text' : ''}`;
      if (dangerouslySetTextAsHTML) {
        return <span className={textClasses} dangerouslySetInnerHTML={{__html: text}}></span>;
      }

      return <span className={textClasses}>{text}</span>;
    };

    const getShortText = (): ReactElement<any> | null => {
      return props.shortText ? <span className="-short">{props.shortText}</span> : null;
    };

    const renderPremiumUpsellIcon = (): ReactElement<any> => {
      return (
        <div className="premium-icon-container">
          <Icon icon="icon-crown" size={getPremiumUpsellIconSize()} className="ct-premium-primary-icon" />
        </div>
      );
    };

    const renderCustomText = (): ReactElement<any> | null => {
      if (props.customText) {
        return props.customText;
      }

      return null;
    };

    return (
      <BtnTag ref={ref} id={id} className={classes.join(' ')} disabled={disabled} data-testid={props['data-testid']} onClick={onClickHandler} title={title} {...attributes}>
        <span className={`btn-text ${textClasses}`}>
          {props.customText ? (
            renderCustomText()
          ) : (
            <>
              {getLongText()}
              {getShortText()}
            </>
          )}
        </span>
        {renderIcon()}
        {props.loadingStateText ? getLoaderWithLoadingStateText() : getLoader()}
        {props.children}
        {showUpsell ? renderPremiumUpsellIcon() : null}
      </BtnTag>
    );
  }
);
